import type { AiSearchEvent, AiSearchPage, AiSearchResult } from '@/shared/api/graphql/aiSearch';
import { AiSearchStep } from '@/shared/api/graphql/aiSearch';
import { create } from 'zustand';

/** The patient a question was asked about, when it isn't the open page's: one picked from a list. */
export type AskPatient = { truentityId: string; name: string | null };

export type AiSearchTurn = {
  id: string;
  question: string;
  patient?: AskPatient;
  /** Set once startAiSearch answers. */
  searchId?: string;
  /** Set when the search could not be started, or never finished. */
  error?: string;
};

export type TurnStatus = 'running' | 'done' | 'failed';

/** `answer`: one question in a dialog. `chat`: a full-screen conversation that keeps every turn. */
export type AiSearchMode = 'answer' | 'chat';

type State = {
  open: boolean;
  mode: AiSearchMode;
  turns: AiSearchTurn[];
  /**
   * Keyed by searchId rather than kept on the turn: the worker can send its first events before
   * the mutation's response has told us which turn they belong to.
   */
  eventsBySearchId: Record<string, AiSearchEvent[]>;
  setOpen: (open: boolean) => void;
  /** Carries on from the current answer as a conversation. */
  startChat: () => void;
  addTurn: (turn: AiSearchTurn) => void;
  attachSearchId: (turnId: string, searchId: string) => void;
  failTurn: (turnId: string, error: string) => void;
  applyEvent: (event: AiSearchEvent) => void;
};

// Questions can name patients, so this store is memory only: never persisted.
export const useAiSearchStore = create<State>(set => ({
  open: false,
  mode: 'answer',
  turns: [],
  eventsBySearchId: {},
  // Closing ends a conversation: the next question from the header is a single answer again.
  setOpen: open => set(open ? { open } : { open, mode: 'answer' }),
  startChat: () => set({ mode: 'chat' }),
  // An answer is one question at a time: a new one replaces the last, and its events with it.
  // A conversation keeps them all.
  addTurn: turn =>
    set(state =>
      state.mode === 'chat' ? { turns: [...state.turns, turn], open: true } : { turns: [turn], eventsBySearchId: {}, open: true }
    ),
  attachSearchId: (turnId, searchId) =>
    set(state => ({ turns: state.turns.map(turn => (turn.id === turnId ? { ...turn, searchId } : turn)) })),
  failTurn: (turnId, error) => set(state => ({ turns: state.turns.map(turn => (turn.id === turnId ? { ...turn, error } : turn)) })),
  applyEvent: event =>
    set(state => {
      const events = state.eventsBySearchId[event.searchId] ?? [];
      if (events.some(existing => existing.seq === event.seq)) return state;

      const sorted = [...events, event].sort((a, b) => a.seq - b.seq);
      return { eventsBySearchId: { ...state.eventsBySearchId, [event.searchId]: sorted } };
    })
}));

const EMPTY: AiSearchEvent[] = [];

export const eventsOf = (turn: AiSearchTurn, eventsBySearchId: State['eventsBySearchId']): AiSearchEvent[] =>
  (turn.searchId && eventsBySearchId[turn.searchId]) || EMPTY;

const finalEvent = (events: AiSearchEvent[]) =>
  events.find(event => event.step === AiSearchStep.COMPLETED || event.step === AiSearchStep.FAILED);

export const turnStatus = (turn: AiSearchTurn, events: AiSearchEvent[]): TurnStatus => {
  if (turn.error) return 'failed';

  const last = finalEvent(events);
  if (!last) return 'running';
  return last.step === AiSearchStep.COMPLETED ? 'done' : 'failed';
};

export const turnResult = (events: AiSearchEvent[]): AiSearchResult | null => finalEvent(events)?.result ?? null;

export const turnSuggestions = (events: AiSearchEvent[]): string[] => finalEvent(events)?.suggestions ?? [];

/** The page the agent named as listing all of what was asked. */
export const turnPage = (events: AiSearchEvent[]): AiSearchPage | null => turnResult(events)?.page ?? null;

export const turnError = (turn: AiSearchTurn, events: AiSearchEvent[]): string | null => turn.error ?? finalEvent(events)?.error ?? null;

/** The step to show while running: the latest one, or none before the worker picks it up. */
export const currentStep = (events: AiSearchEvent[]): string | null => events[events.length - 1]?.step ?? null;
